/**
 * Formik-bound QueryBuilder wrapper for guardrail rules.
 * Sibling of `WorkflowConditionsQueryBuilderFormik` — same skeleton (read
 * `values[fieldId]`, gate on `submitCount`, compute prereqs, call the shared
 * registry hook, render the pure QB) parameterised over the `guardrail_rule`
 * rule_set_type (registered by the guardrails module on the backend).
 *
 * Guardrail rules trigger on a `trigger_event`, so the catalog fetch passes the
 * event through and the BE narrows the field registry by event scope. Prereq
 * gate matches the workflow wrapper: an event and a LOB scope (concrete LOBs or
 * the all-LOBs flag) plus a sub-org scope must be picked before the catalog is
 * fetched.
 *
 * The composed pure `RuleConditionsQueryBuilder` writes its tree into Formik
 * under `fieldId` (default `'conditions_json'`).
 */

import React, { useEffect, useMemo } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import useOrganization from '../../../../OrganizationContext';
import { checkConditionPrerequisites } from '../../Conditions/QueryBuilder/prerequisites';
import RuleConditionsQueryBuilder from '../../Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import { useRuleSetConditionFieldRegistry } from '../../Conditions/QueryBuilder/useRuleSetConditionFieldRegistry';

const GUARDRAIL_RULE_TYPE = 'guardrail_rule';

interface GuardrailConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** Event firing the rule. Empty string = "not yet picked" — the catalog fetch
   *  is gated until the user selects an event (the BE needs it to filter the
   *  catalog by event scope). */
  eventKey: string;
  /** Every LOB the rule is scoped to. Forwarded whole: the catalog is the
   *  cross-LOB intersection, and a rule offered one LOB's fields can be saved
   *  with conditions the other LOBs' claims never carry. */
  lobs: string[];
  /** True when the rule is scoped to every LOB, present and future. Satisfies
   *  the LOB prerequisite on its own and is forwarded as `is_all_lobs`. */
  isAllLobs: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Surfaces the catalog-fetch loading state so the parent dialog can disable
   *  Save while fields are still in flight. */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Condition fields that are part of the rule's definition rather than an author's choice, and so
   *  must render locked — no editing, no delete. Set by the claim-action dialog for the AI-agent
   *  leaf every guardrail there carries (removing it would let the rule gate manual actions too) and
   *  for the action's required conditions. */
  lockedFieldPaths?: string[];
}

const GuardrailConditionsQueryBuilderFormik: React.FC<GuardrailConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  eventKey,
  lobs,
  isAllLobs,
  isAllSubOrgs,
  subOrgIds,
  onLoadingChange,
  lockedFieldPaths,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // `useOrganization` is exported from a JSX file without types; cast at the boundary.
  const { organizationId, subOrganizationEnabled } = useOrganization() as {
    organizationId: number;
    subOrganizationEnabled?: boolean;
  };

  const { prerequisitesMet, prerequisiteMessage } = checkConditionPrerequisites(
    eventKey,
    isAllLobs,
    lobs,
    isAllSubOrgs,
    subOrgIds,
    !!subOrganizationEnabled
  );

  const { fields, isLoading } = useRuleSetConditionFieldRegistry({
    organizationId,
    enabled: prerequisitesMet,
    ruleSetType: GUARDRAIL_RULE_TYPE,
    eventKey,
    lobs,
    isAllLobs,
    isAllSubOrgs,
    subOrgIds,
  });

  // Depend on the boolean only, not the callback identity (parent wires a stable
  // useState setter). Including the callback could infinite-loop on inline arrows.
  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Scanned rather than pinned to index 0: the locked leaf is seeded first, but reading the tree
  // means a rule saved with it elsewhere in the list still locks, instead of locking whichever
  // condition happens to sit first. Top level only — the seed is never nested, and a leaf the
  // author moved into a group is theirs to manage.
  const disabledPaths = useMemo(() => {
    if (!lockedFieldPaths?.length || !value) {
      return undefined;
    }
    const paths = (value.rules ?? []).reduce<number[][]>((acc, rule, index) => {
      const { field } = rule as { field?: string };
      if (!Array.isArray((rule as RuleGroupType).rules) && field !== undefined && lockedFieldPaths.includes(field)) {
        acc.push([index]);
      }
      return acc;
    }, []);
    return paths.length ? paths : undefined;
  }, [lockedFieldPaths, value]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      disabledPaths={disabledPaths}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage="No condition fields available for this event and LOB."
      // A guardrail triggers on its `trigger_event`, so it always has an event time.
      hasTriggeringEvent
    />
  );
};

export default GuardrailConditionsQueryBuilderFormik;
